import type { FakeFirm, FakeMatter, FakeThread } from "./types";

export const FAKE_FIRM: FakeFirm = {
	id: "firm-martinez",
	name: "Martinez & Associates",
	users: [
		{
			id: "user-priya",
			name: "Priya Patel",
			email: "priya@martinezlaw.com",
			role: "paralegal",
			emailAttached: true,
		},
		{
			id: "user-marcus",
			name: "Marcus Chen",
			email: "marcus@martinezlaw.com",
			role: "attorney",
			emailAttached: true,
		},
		{
			id: "user-jennifer",
			name: "Jennifer Okafor",
			email: "jennifer@martinezlaw.com",
			role: "paralegal",
			emailAttached: true,
		},
		{
			id: "user-diego",
			name: "Diego Morales",
			email: "diego@martinezlaw.com",
			role: "intake",
			emailAttached: false,
		},
	],
};

export const FAKE_MATTERS: Record<string, FakeMatter> = {
	"matter-silva-i485": {
		id: "matter-silva-i485",
		clientName: "Roberto Silva",
		matterType: "Marriage-based AOS (I-485)",
		stage: "Filed — awaiting biometrics",
		responsibleAttorney: "Marcus Chen",
		paralegal: "Priya Patel",
		openedAt: "2025-11-14",
		receiptNumber: "MSC2590123456",
		summary:
			"Roberto Silva (Brazilian national, US entry on B-2 2023) filed I-130/I-485 concurrently with USC spouse Jessica Silva on 2026-01-08. I-130 and I-485 receipted 2026-01-19. EAD/AP combo card approved 2026-03-22. Awaiting biometrics appointment notice.",
		contacts: [
			{
				name: "Roberto Silva",
				role: "Beneficiary / client",
				email: "roberto.silva@example.com",
				phone: "(305) 555-0764",
			},
			{
				name: "Jessica Silva",
				role: "USC petitioner (spouse)",
				email: "jsilva82@example.com",
				phone: "(305) 555-0712",
			},
		],
		timeline: [
			{
				date: "2025-11-14",
				label: "Intake call",
				detail: "60 min consult with Marcus. Retained same day.",
				source: "clio",
			},
			{
				date: "2025-12-03",
				label: "Engagement agreement signed",
				source: "clio",
			},
			{
				date: "2026-01-08",
				label: "I-130 / I-485 / I-765 / I-131 packet mailed to Chicago Lockbox",
				source: "clio",
			},
			{
				date: "2026-01-19",
				label: "Receipt notices received (I-130, I-485)",
				source: "email",
			},
			{
				date: "2026-03-22",
				label: "EAD/AP combo card approved",
				source: "email",
			},
			{
				date: "2026-04-03",
				label: "Client called re: biometrics delay",
				detail: "Priya took call, explained typical 8–12 week wait.",
				source: "clio",
			},
		],
		openTasks: [
			{
				title: "Confirm biometrics appointment when notice arrives",
				assignee: "Priya Patel",
			},
			{
				title: "Update client address with USCIS (AR-11)",
				assignee: "Priya Patel",
				dueDate: "2026-04-22",
			},
		],
		closedTasks: [
			{
				title: "Prepare I-130/485 packet",
				assignee: "Priya Patel",
				completedDate: "2026-01-06",
			},
			{
				title: "Attorney review of packet",
				assignee: "Marcus Chen",
				completedDate: "2026-01-07",
			},
		],
		documents: [
			{
				name: "I-130 Packet Final.pdf",
				path: "/Clients/Silva, Roberto/Filings/",
				size: "4.2 MB",
				modifiedAt: "2026-01-07",
			},
			{
				name: "I-485 Packet Final.pdf",
				path: "/Clients/Silva, Roberto/Filings/",
				size: "6.8 MB",
				modifiedAt: "2026-01-07",
			},
			{
				name: "Receipt Notice I-797 I-130.pdf",
				path: "/Clients/Silva, Roberto/USCIS Notices/",
				size: "210 KB",
				modifiedAt: "2026-01-19",
			},
			{
				name: "EAD Approval Notice.pdf",
				path: "/Clients/Silva, Roberto/USCIS Notices/",
				size: "180 KB",
				modifiedAt: "2026-03-22",
			},
			{
				name: "Passport - Roberto Silva.pdf",
				path: "/Clients/Silva, Roberto/Client Documents/",
				size: "1.1 MB",
				modifiedAt: "2025-12-15",
			},
			{
				name: "Marriage Certificate.pdf",
				path: "/Clients/Silva, Roberto/Client Documents/",
				size: "340 KB",
				modifiedAt: "2025-11-28",
			},
		],
		customFields: [
			{ label: "Priority Date", value: "2026-01-08" },
			{ label: "Service Center", value: "National Benefits Center" },
			{ label: "A-Number", value: "A-089-234-567" },
			{ label: "Country of birth", value: "Brazil" },
		],
		notes: [
			{
				author: "Marcus Chen",
				date: "2025-11-14",
				body: "Strong case. Two years married, shared lease + joint bank from Feb 2025, three years of joint return filings. No prior immigration history. Standard AOS workflow.",
			},
			{
				author: "Priya Patel",
				date: "2026-04-03",
				body: "Client anxious about biometrics delay. Explained typical 8–12 wk window, reassured EAD/AP is in hand so no travel/work issues. Will flag if notice doesn't arrive by 2026-04-30.",
			},
		],
	},

	"matter-wilson-k1": {
		id: "matter-wilson-k1",
		clientName: "James Wilson & Thanh Nguyen",
		matterType: "K-1 Fiancé (Consular Processing)",
		stage: "221(g) refusal — additional evidence requested",
		responsibleAttorney: "Marcus Chen",
		paralegal: "Jennifer Okafor",
		openedAt: "2025-06-20",
		receiptNumber: "WAC2425098765",
		summary:
			"James Wilson (USC) petitioned for fiancée Thanh Nguyen (Vietnamese national). I-129F approved 2025-11-04. NVC case completed 2026-01-22. Interview at U.S. Consulate Ho Chi Minh City 2026-04-15 — 221(g) refusal issued, case returned for additional relationship evidence.",
		contacts: [
			{
				name: "James Wilson",
				role: "USC petitioner",
				email: "james.wilson@example.com",
				phone: "(512) 555-0143",
			},
			{
				name: "Thanh Nguyen",
				role: "Beneficiary fiancée",
				email: "thanh.nguyen.hcm@example.com",
				phone: "+84 28 555 0198",
			},
		],
		timeline: [
			{
				date: "2025-06-20",
				label: "Intake consult",
				source: "clio",
			},
			{
				date: "2025-07-05",
				label: "I-129F filed",
				source: "clio",
			},
			{
				date: "2025-11-04",
				label: "I-129F approved",
				source: "email",
			},
			{
				date: "2025-12-18",
				label: "NVC case created, DS-160 submitted",
				source: "clio",
			},
			{
				date: "2026-01-22",
				label: "NVC case complete — forwarded to HCMC",
				source: "email",
			},
			{
				date: "2026-04-15",
				label: "Interview at USCG HCMC — 221(g) refusal",
				detail: "Additional relationship evidence requested.",
				source: "email",
			},
		],
		openTasks: [
			{
				title: "Review 221(g) paper from consulate",
				assignee: "Marcus Chen",
				dueDate: "2026-04-19",
			},
			{
				title: "Draft evidence response plan",
				assignee: "Jennifer Okafor",
				dueDate: "2026-04-24",
			},
			{
				title: "Request additional relationship evidence from James",
				assignee: "Jennifer Okafor",
			},
		],
		closedTasks: [
			{
				title: "I-129F packet",
				assignee: "Jennifer Okafor",
				completedDate: "2025-07-03",
			},
			{
				title: "DS-160 prep session with Thanh",
				assignee: "Jennifer Okafor",
				completedDate: "2025-12-10",
			},
		],
		documents: [
			{
				name: "I-129F Packet.pdf",
				path: "/Clients/Wilson-Nguyen/Filings/",
				size: "5.4 MB",
				modifiedAt: "2025-07-03",
			},
			{
				name: "I-129F Approval Notice.pdf",
				path: "/Clients/Wilson-Nguyen/USCIS Notices/",
				size: "195 KB",
				modifiedAt: "2025-11-04",
			},
			{
				name: "Affidavit of Support Packet.pdf",
				path: "/Clients/Wilson-Nguyen/Financial/",
				size: "2.1 MB",
				modifiedAt: "2025-12-14",
			},
			{
				name: "Relationship Evidence - Chat Logs.pdf",
				path: "/Clients/Wilson-Nguyen/Relationship Evidence/",
				size: "12.4 MB",
				modifiedAt: "2025-12-11",
			},
			{
				name: "Trip Photos Oct 2024.zip",
				path: "/Clients/Wilson-Nguyen/Relationship Evidence/",
				size: "28.1 MB",
				modifiedAt: "2025-07-02",
			},
		],
		customFields: [
			{ label: "Priority Date", value: "2025-07-05" },
			{ label: "Consulate", value: "Ho Chi Minh City, Vietnam" },
			{ label: "Beneficiary DOB", value: "1994-03-17" },
			{ label: "Country of birth", value: "Vietnam" },
		],
		notes: [
			{
				author: "Marcus Chen",
				date: "2026-04-15",
				body: "James called same day as interview. Consular officer flagged thin relationship evidence (primarily digital). Need: more in-person proof (trip itineraries, flight records, photos with dates), statements from family/friends, evidence of ongoing financial support. Typical 221(g) — not a denial. Budget 2–3 weeks to reassemble.",
			},
		],
	},

	"matter-kowalski-n400": {
		id: "matter-kowalski-n400",
		clientName: "Anna Kowalski",
		matterType: "Naturalization (N-400)",
		stage: "Interview passed — awaiting oath ceremony",
		responsibleAttorney: "Marcus Chen",
		paralegal: "Priya Patel",
		openedAt: "2025-02-11",
		receiptNumber: "NBC2525011234",
		summary:
			"Anna Kowalski (LPR since 2020, Polish national). N-400 filed 2025-05-03 based on 5-year rule. Biometrics 2025-07-18. Interview 2026-03-28 — application approved on-the-spot. Awaiting oath ceremony scheduling.",
		contacts: [
			{
				name: "Anna Kowalski",
				role: "Applicant",
				email: "anna.k.chicago@example.com",
				phone: "(773) 555-0188",
			},
		],
		timeline: [
			{
				date: "2025-02-11",
				label: "Intake + eligibility review",
				source: "clio",
			},
			{
				date: "2025-05-03",
				label: "N-400 filed online",
				source: "clio",
			},
			{
				date: "2025-07-18",
				label: "Biometrics appointment",
				source: "email",
			},
			{
				date: "2026-03-28",
				label: "Interview — approved",
				source: "email",
			},
		],
		openTasks: [
			{
				title: "Confirm oath ceremony date when notice arrives",
				assignee: "Priya Patel",
			},
		],
		closedTasks: [
			{
				title: "Civics/English prep session",
				assignee: "Priya Patel",
				completedDate: "2026-03-20",
			},
			{
				title: "Interview day prep call",
				assignee: "Marcus Chen",
				completedDate: "2026-03-27",
			},
		],
		documents: [
			{
				name: "N-400 Application.pdf",
				path: "/Clients/Kowalski, Anna/Filings/",
				size: "2.8 MB",
				modifiedAt: "2025-05-03",
			},
			{
				name: "Green Card - front-back.pdf",
				path: "/Clients/Kowalski, Anna/Client Documents/",
				size: "220 KB",
				modifiedAt: "2025-02-15",
			},
			{
				name: "Interview Approval Form N-652.pdf",
				path: "/Clients/Kowalski, Anna/USCIS Notices/",
				size: "140 KB",
				modifiedAt: "2026-03-28",
			},
		],
		customFields: [
			{ label: "A-Number", value: "A-072-118-443" },
			{ label: "Field Office", value: "Chicago" },
			{ label: "Eligibility basis", value: "5-year LPR" },
		],
		notes: [
			{
				author: "Marcus Chen",
				date: "2026-03-28",
				body: "Interview passed first try. Strong civics performance. Officer was efficient, no surprises. Oath typically 2–6 wks out in Chicago.",
			},
		],
	},

	"matter-patel-i751": {
		id: "matter-patel-i751",
		clientName: "Aarav Patel",
		matterType: "Removal of Conditions (I-751)",
		stage: "Filed — awaiting biometrics waiver or notice",
		responsibleAttorney: "Marcus Chen",
		paralegal: "Priya Patel",
		openedAt: "2025-10-02",
		receiptNumber: "MSC2590087654",
		summary:
			"Aarav Patel (Indian national, CR-1 GC obtained 2024-02-14) filing joint I-751 with USC spouse Meera Iyer. Filed within 90-day window on 2026-01-20. Receipt extends LPR status 48 months.",
		contacts: [
			{
				name: "Aarav Patel",
				role: "Client",
				email: "aarav.patel@example.com",
				phone: "(408) 555-0117",
			},
			{
				name: "Meera Iyer",
				role: "USC spouse / co-petitioner",
				email: "meera.iyer@example.com",
			},
		],
		timeline: [
			{
				date: "2025-10-02",
				label: "Intake / engagement",
				source: "clio",
			},
			{
				date: "2026-01-20",
				label: "I-751 joint petition filed",
				source: "clio",
			},
			{
				date: "2026-02-02",
				label: "Receipt notice — 48-month extension",
				source: "email",
			},
		],
		openTasks: [
			{
				title: "Watch for biometrics waiver or notice",
				assignee: "Priya Patel",
			},
		],
		closedTasks: [],
		documents: [
			{
				name: "I-751 Packet.pdf",
				path: "/Clients/Patel, Aarav/Filings/",
				size: "5.9 MB",
				modifiedAt: "2026-01-18",
			},
			{
				name: "I-751 Receipt Notice.pdf",
				path: "/Clients/Patel, Aarav/USCIS Notices/",
				size: "180 KB",
				modifiedAt: "2026-02-02",
			},
			{
				name: "Joint Bank Statements 2024-2025.pdf",
				path: "/Clients/Patel, Aarav/Relationship Evidence/",
				size: "3.3 MB",
				modifiedAt: "2026-01-12",
			},
		],
		customFields: [
			{ label: "A-Number", value: "A-210-445-112" },
			{ label: "Service Center", value: "National Benefits Center" },
			{ label: "Conditional GC issued", value: "2024-02-14" },
		],
		notes: [
			{
				author: "Priya Patel",
				date: "2026-01-20",
				body: "Strong joint evidence — joint mortgage, 2 joint returns, shared health insurance, 3 trip photos, birth of daughter in 2025. Low risk.",
			},
		],
	},

	"matter-reyes-vawa": {
		id: "matter-reyes-vawa",
		clientName: "Maria Reyes (inquiry)",
		matterType: "VAWA Self-Petition (inquiry)",
		stage: "Pre-engagement — intake pending",
		responsibleAttorney: "Marcus Chen",
		paralegal: "Jennifer Okafor",
		openedAt: "2026-04-16",
		summary:
			"Inbound inquiry from third party (Sofia Reyes, emailing on Maria's behalf). Maria is subject of GC petition by USC spouse; spouse is threatening withdrawal and there is apparent domestic abuse. Potential VAWA eligibility. No engagement yet — need secure intake.",
		contacts: [
			{
				name: "Sofia Reyes",
				role: "Contact (sister of inquirer)",
				email: "sofia@example.com",
				phone: "(832) 555-0219",
			},
			{
				name: "Maria (last name withheld)",
				role: "Potential client",
			},
		],
		timeline: [
			{
				date: "2026-04-16",
				label: "Inbound email from Sofia Reyes",
				detail: "Asking for confidential consult; no phone access.",
				source: "email",
			},
		],
		openTasks: [
			{
				title: "Coordinate safe intake call via Sofia's phone",
				assignee: "Jennifer Okafor",
				dueDate: "2026-04-20",
			},
			{
				title: "Prepare VAWA preliminary screening checklist",
				assignee: "Jennifer Okafor",
			},
		],
		closedTasks: [],
		documents: [],
		customFields: [
			{ label: "Inquiry source", value: "Email (via sister)" },
			{ label: "Safety concern", value: "Yes — handle sensitively" },
		],
		notes: [
			{
				author: "Jennifer Okafor",
				date: "2026-04-16",
				body: "Flagged to Marcus for same-day awareness. Will reply via sister's email with a number and a plan to not leave a voicemail that could be intercepted.",
			},
		],
	},

	"matter-chen-h1b": {
		id: "matter-chen-h1b",
		clientName: "Wei Chen",
		matterType: "H-1B Transfer",
		stage: "Pre-filing — gathering LCA and support letter",
		responsibleAttorney: "Marcus Chen",
		paralegal: "Priya Patel",
		openedAt: "2026-03-28",
		summary:
			"Wei Chen currently H-1B with Amazon; accepted offer at Stripe (new employer). Stripe to sponsor H-1B transfer. LCA posting started 2026-04-10. Target premium processing filing by 2026-04-26.",
		contacts: [
			{
				name: "Wei Chen",
				role: "Beneficiary",
				email: "wei.chen@example.com",
				phone: "(415) 555-0301",
			},
			{
				name: "HR — Stripe (Lena Fields)",
				role: "New employer contact",
				email: "lena.fields@stripe.example.com",
			},
		],
		timeline: [
			{
				date: "2026-03-28",
				label: "Intake w/ Wei",
				source: "clio",
			},
			{
				date: "2026-04-02",
				label: "Engagement from Stripe",
				source: "clio",
			},
			{
				date: "2026-04-10",
				label: "LCA posted at worksite",
				source: "clio",
			},
		],
		openTasks: [
			{
				title: "Finalize LCA",
				assignee: "Priya Patel",
				dueDate: "2026-04-20",
			},
			{
				title: "Collect support letter draft from Stripe",
				assignee: "Priya Patel",
				dueDate: "2026-04-22",
			},
			{
				title: "File I-129 w/ premium processing",
				assignee: "Marcus Chen",
				dueDate: "2026-04-26",
			},
		],
		closedTasks: [],
		documents: [
			{
				name: "Wei Chen Current I-797.pdf",
				path: "/Clients/Chen, Wei/Client Documents/",
				size: "215 KB",
				modifiedAt: "2026-03-30",
			},
		],
		customFields: [
			{ label: "New employer", value: "Stripe, Inc." },
			{ label: "Role", value: "Senior Software Engineer" },
			{ label: "Target file date", value: "2026-04-26" },
		],
		notes: [],
	},
};

// Threads

const TIM_EMAIL = { name: "Priya Patel", email: "priya@martinezlaw.com" };
const MARCUS_EMAIL = { name: "Marcus Chen", email: "marcus@martinezlaw.com" };
const JENNIFER_EMAIL = {
	name: "Jennifer Okafor",
	email: "jennifer@martinezlaw.com",
};

export const FAKE_THREADS: FakeThread[] = [
	// Priya's inbox
	{
		id: "thr-001",
		conversationId: "conv-roberto-biometrics",
		userId: "user-priya",
		subject: "Please call me",
		hasReply: false,
		matterMatch: { matterId: "matter-silva-i485" },
		tags: [
			{ label: "status inquiry", axis: "intent" },
			{ label: "filed-waiting", axis: "stage" },
			{ label: "existing client", axis: "who" },
		],
		messages: [
			{
				id: "msg-001-1",
				direction: "inbound",
				from: { name: "Roberto Silva", email: "roberto.silva@example.com" },
				to: [TIM_EMAIL],
				receivedAt: "2026-04-18T21:50:42Z",
				hasAttachments: false,
				bodyText:
					"Hi,\n\nI've been trying to reach someone at your office for the past two days. I called three times yesterday and twice today and I keep getting voicemail. I left messages but nobody called me back.\n\nI have a situation with my case that I need to discuss and it's kind of time sensitive. I got a letter that says something about \"biometrics\" and I don't know what that means. Also my address changed and I don't know if I was supposed to tell someone.\n\nMy number is (305) 555-0764.\n\nRoberto Silva",
			},
		],
		fakeDraft: {
			targetMessageId: "msg-001-1",
			model: "claude-sonnet-5",
			promptVersion: "drafter-v0.3 · practice-guide-v0",
			generatedAt: "2026-04-19T19:40:00Z",
			toolCalls: [
				{
					id: "tc-001-1",
					toolName: "lookupMatter",
					title: "Looking up matter",
					input: { senderEmail: "roberto.silva@example.com" },
					output: {
						matterId: "MSC2590123456",
						clientName: "Roberto Silva",
						matterType: "I-485",
						stage: "Filed — awaiting biometrics",
						responsibleAttorney: "Priya Patel",
						lastActivity: "2026-04-03 — client call about biometrics delay",
					},
				},
				{
					id: "tc-001-2",
					toolName: "searchPracticeGuide",
					title: "Searching practice guide",
					input: { query: "I-485 biometrics notice timeline" },
					output: {
						source: "§AOS-biometrics-timeline",
						snippet:
							"Typical NBC biometrics window is 8–12 weeks after receipt. If the notice has arrived, forward it; we confirm appointment and instructions on day-of.",
					},
				},
				{
					id: "tc-001-3",
					toolName: "searchPracticeGuide",
					title: "Searching practice guide",
					input: { query: "address change AR-11" },
					output: {
						source: "§AOS-address-change",
						snippet:
							"File AR-11 within 10 days of move. Also update USCIS online account. Keep stamped AR-11 confirmation with the matter file in case notices were mis-mailed.",
					},
				},
				{
					id: "tc-001-4",
					toolName: "searchFirmPolicy",
					title: "Searching firm policy",
					input: { query: "unreturned client calls" },
					output: {
						source: "§phone-responsiveness",
						snippet:
							"When a client reports multiple unreturned calls, acknowledge the gap explicitly and offer a next-business-day call slot.",
					},
				},
			],
			bodyText:
				"Hi Roberto,\n\nI'm so sorry — I see your calls in our system and want to apologize for the delay getting back to you. Let me handle both things you raised.\n\n1. Biometrics. If the letter you received is on USCIS letterhead and mentions a date and a location (an ASC), that's your biometrics appointment notice. Nothing is wrong — it's a routine step in your I-485 process. Please scan and send me the notice and I'll confirm the date, what to bring, and what to expect. If it's a different kind of notice, send it anyway and I'll translate it.\n\n2. Address change. Yes — we need to update your address with USCIS within 10 days of your move. I'll file an AR-11 on your behalf today if you send me:\n   • Your new street address, including apartment number\n   • The date you moved in\n\nI can also call you tomorrow (4/20) between 10 AM and 12 PM at (305) 555-0764 if you'd prefer to handle this by phone. Let me know what works.\n\nPriya",
		},
	},
	{
		id: "thr-002",
		conversationId: "conv-wilson-221g",
		userId: "user-priya",
		subject: "Fiancee visa interview didn't go well",
		hasReply: true,
		matterMatch: { matterId: "matter-wilson-k1" },
		tags: [
			{ label: "refusal / RFE", axis: "intent" },
			{ label: "consular interview", axis: "stage" },
			{ label: "existing client", axis: "who" },
		],
		fakeDraft: {
			targetMessageId: "msg-002-1",
			model: "claude-sonnet-5",
			promptVersion: "drafter-v0.3 · practice-guide-v0",
			generatedAt: "2026-04-19T19:42:00Z",
			toolCalls: [
				{
					id: "tc-002-1",
					toolName: "lookupMatter",
					title: "Looking up matter",
					input: { senderEmail: "james.wilson@example.com" },
					output: {
						matterId: "matter-wilson-k1",
						clientName: "James Wilson & Thanh Nguyen",
						matterType: "K-1 fiancée visa",
						stage: "Consular interview — 221(g) additional evidence",
						responsibleAttorney: "Marcus Brown",
					},
				},
				{
					id: "tc-002-2",
					toolName: "searchPracticeGuide",
					title: "Searching practice guide",
					input: { query: "K-1 221(g) response" },
					output: {
						source: "§K1-221g",
						snippet:
							"221(g) is a refusal pending additional evidence, NOT a denial. Clients often panic — lead with reassurance and a concrete plan. Typical response window: 1 year from refusal date.",
					},
				},
				{
					id: "tc-002-3",
					toolName: "searchPracticeGuide",
					title: "Searching practice guide",
					input: { query: "K-1 relationship evidence" },
					output: {
						source: "§K1-relationship-evidence",
						snippet:
							"When consulate flags thin relationship evidence, emphasize IN-PERSON proof: flight itineraries, boarding passes, dated photos with location metadata, statements from family/friends who have met both parties.",
					},
				},
				{
					id: "tc-002-4",
					toolName: "getPriorThreads",
					title: "Reading prior threads",
					input: { matterId: "matter-wilson-k1", limit: 3 },
					output: {
						source: "Wilson thread 2026-01-22",
						snippet:
							"Prior emails from James regarding NVC case completion and interview prep. Known anxious client; responds well to clear numbered action items.",
					},
				},
			],
			bodyText:
				"James,\n\nI'm sorry to hear about yesterday. I want to reassure you up front: a 221(g) is not a denial. It is a request for additional evidence — very common at consular interviews, especially when the officer feels the relationship proof is primarily digital. This is absolutely fixable.\n\nTo move fast:\n\n1. Please forward the 221(g) paper as soon as Thanh scans it — I'll read the exact language and we'll know exactly what they're asking for.\n2. In the meantime, start pulling together anything that shows in-person time together with dates: flight itineraries, boarding passes, hotel receipts, dated photos, texts/emails with visible dates.\n3. If any family members or friends can write a short statement that they've met you both together, those help.\n\nI've copied Marcus so he can review the paper with me once it comes in. Typical turnaround to re-submit is 2–3 weeks from when we have everything.\n\nHang in there. We'll get this done.\n\nPriya",
		},
		messages: [
			{
				id: "msg-002-1",
				direction: "inbound",
				from: { name: "James Wilson", email: "james.wilson@example.com" },
				to: [TIM_EMAIL],
				cc: [MARCUS_EMAIL],
				receivedAt: "2026-04-15T21:50:36Z",
				hasAttachments: false,
				bodyText:
					"Tim,\n\nSo my fiancée Thanh had her K-1 interview at the consulate in Ho Chi Minh City yesterday and it did not go well. They told her the case is \"refused\" and gave her a paper that says something about section 221(g) and that she needs to provide additional documentation. The paper is mostly in English but she's not sure what they actually want.\n\nI don't understand because we already sent everything. We sent photos from when I visited, our chat logs, the whole affidavit of support, all of it. We've been together for two years and I've visited her three times.\n\nShe's really upset and honestly so am I. We were supposed to be planning our wedding.\n\nWhat do we do now? Is this a denial or is it something we can fix? She's going to scan the paper they gave her and I'll forward it when I get it.\n\nJames Wilson",
			},
			{
				id: "msg-002-2",
				direction: "outbound",
				from: TIM_EMAIL,
				to: [{ name: "James Wilson", email: "james.wilson@example.com" }],
				cc: [MARCUS_EMAIL],
				receivedAt: "2026-04-16T14:12:00Z",
				hasAttachments: false,
				bodyText:
					"James,\n\nI'm sorry to hear about yesterday. I want to reassure you: a 221(g) is not a denial. It is a request for additional evidence and is very common — especially when the officer feels the relationship evidence is thin. It can absolutely be fixed.\n\nTo move fast:\n\n1. Please forward the 221(g) paper as soon as Thanh scans it — I'll read the exact language and we'll know what they're asking for.\n2. In the meantime, start pulling together anything that shows in-person time together with dates: flight itineraries, boarding passes, hotel receipts, dated photos, texts/emails with visible dates.\n3. If any family members or friends can write a short statement that they've met you both together, those help.\n\nI've copied Marcus so he can review the paper with me once it comes in. Typical turnaround to re-submit is 2–3 weeks from when we have everything.\n\nHang in there. We'll get this done.\n\nPriya",
			},
			{
				id: "msg-002-3",
				direction: "inbound",
				from: { name: "James Wilson", email: "james.wilson@example.com" },
				to: [TIM_EMAIL],
				receivedAt: "2026-04-16T17:45:00Z",
				hasAttachments: true,
				bodyText:
					"Priya — thank you so much, that is a huge relief. Attached is the 221(g) paper Thanh scanned this morning. Starting on the rest tonight.\n\nJames",
			},
		],
	},
	{
		id: "thr-003",
		conversationId: "conv-sofia-vawa-inquiry",
		userId: "user-priya",
		subject: "asking for a friend - immigration question",
		hasReply: false,
		matterMatch: { matterId: "matter-reyes-vawa" },
		tags: [
			{ label: "new lead", axis: "intent" },
			{ label: "sensitive", axis: "intent" },
			{ label: "prospective client", axis: "who" },
		],
		fakeDraft: {
			targetMessageId: "msg-003-1",
			model: "claude-sonnet-5",
			promptVersion: "drafter-v0.3 · practice-guide-v0",
			generatedAt: "2026-04-19T19:45:00Z",
			toolCalls: [
				{
					id: "tc-003-1",
					toolName: "lookupMatter",
					title: "Looking up matter",
					input: { senderEmail: "sofia@example.com" },
					output: {
						match: false,
						note: "Third-party sender. Potential client (not named) may relate to existing Reyes VAWA pre-engagement file — verify at intake.",
					},
				},
				{
					id: "tc-003-2",
					toolName: "searchFirmPolicy",
					title: "Searching firm policy",
					input: { query: "DV VAWA sensitive intake" },
					output: {
						source: "§sensitive-intake",
						snippet:
							"For suspected DV/VAWA inquiries: respond briefly. Confirm we can speak confidentially. Schedule via the safe contact the inquirer has provided. Do NOT describe legal eligibility theories in the reply — reserve for voice.",
					},
				},
				{
					id: "tc-003-3",
					toolName: "searchPracticeGuide",
					title: "Searching practice guide",
					input: { query: "VAWA self-petition eligibility" },
					output: {
						source: "§VAWA-eligibility-brief",
						snippet:
							"VAWA self-petition allows abused spouse of USC/LPR to self-petition without abuser involvement. Confidentiality protections apply. Intake requires careful screening for eligibility, safety, and evidence.",
					},
				},
			],
			bodyText:
				"Sofia,\n\nThank you for reaching out on your friend's behalf. Yes — we can absolutely speak with her confidentially, and there are options worth discussing that do not require her spouse's involvement.\n\nSo we can talk without putting anything sensitive in email, I'd like to call you at (832) 555-0219 at a time when you can pass the phone to her in a safe location. Please reply with a window that works — morning or evening both fine on our end this week.\n\nIf email is the only safe channel, let me know and we'll work with that.\n\nPriya Patel\nMartinez & Associates",
		},
		messages: [
			{
				id: "msg-003-1",
				direction: "inbound",
				from: { name: "Sofia Reyes", email: "sofia@example.com" },
				to: [TIM_EMAIL],
				receivedAt: "2026-04-16T21:50:36Z",
				hasAttachments: false,
				bodyText:
					"Hi,\n\nI'm emailing on behalf of a friend. She doesn't want me to use her name. She is married to a US citizen and he sponsored her for a green card but the process isn't done yet and he's been threatening to withdraw it.\n\nThe situation at home is not good. I don't want to put too much in an email. She's afraid that if she leaves she'll be deported and lose her kids. He tells her that all the time.\n\nSomeone told her there might be a way to get her own green card without him being involved? Even though he's the one who filed for her?\n\nShe wants to know if she can talk to someone confidentially. She doesn't have access to money and she can't use her own phone for this. I'm emailing from my account.\n\nShe can be reached through me at (832) 555-0219.\n\nThank you,\nSofia",
			},
		],
	},
	{
		id: "thr-004",
		conversationId: "conv-anna-oath",
		userId: "user-priya",
		subject: "When is my oath ceremony?",
		hasReply: true,
		matterMatch: { matterId: "matter-kowalski-n400" },
		tags: [
			{ label: "status inquiry", axis: "intent" },
			{ label: "timeline question", axis: "intent" },
			{ label: "post-interview", axis: "stage" },
			{ label: "existing client", axis: "who" },
		],
		fakeDraft: {
			targetMessageId: "msg-004-1",
			model: "claude-sonnet-5",
			promptVersion: "drafter-v0.3 · practice-guide-v0",
			generatedAt: "2026-04-19T19:50:00Z",
			toolCalls: [
				{
					id: "tc-004-1",
					toolName: "lookupMatter",
					title: "Looking up matter",
					input: { senderEmail: "anna.k.chicago@example.com" },
					output: {
						matterId: "matter-kowalski-n400",
						clientName: "Anna Kowalski",
						matterType: "N-400 naturalization",
						stage: "Interview passed — awaiting oath",
						fieldOffice: "Chicago",
					},
				},
				{
					id: "tc-004-2",
					toolName: "searchPracticeGuide",
					title: "Searching practice guide",
					input: { query: "N-400 oath cadence Chicago" },
					output: {
						source: "§N400-oath-cadence",
						snippet:
							"Post-interview oath ceremony in Chicago field office typically scheduled 2–6 weeks after approval. Notice arrives by mail and appears in the USCIS online account.",
					},
				},
				{
					id: "tc-004-3",
					toolName: "searchFirmPolicy",
					title: "Searching firm policy",
					input: { query: "USCIS timeline commitments" },
					output: {
						source: "§uscis-timelines",
						snippet:
							"Never commit to a specific USCIS date. Always frame as a range based on historical data, and instruct the client to forward any notice immediately.",
					},
				},
			],
			bodyText:
				"Hi Anna — great to hear from you. Chicago oath ceremonies are typically scheduled 2–6 weeks after an approved interview, so you're on the earlier end of that window. The notice will come by mail and also show up in your USCIS online account. As soon as it arrives, please forward it to me and we'll confirm the date and what to bring. No action needed on your end right now.\n\nPriya",
		},
		messages: [
			{
				id: "msg-004-1",
				direction: "inbound",
				from: { name: "Anna Kowalski", email: "anna.k.chicago@example.com" },
				to: [TIM_EMAIL],
				receivedAt: "2026-04-14T15:22:00Z",
				hasAttachments: false,
				bodyText:
					"Hello Priya,\n\nIt's been about two and a half weeks since my interview. When should I expect the oath ceremony? I don't want to miss any notice.\n\nThank you,\nAnna",
			},
			{
				id: "msg-004-2",
				direction: "outbound",
				from: TIM_EMAIL,
				to: [{ name: "Anna Kowalski", email: "anna.k.chicago@example.com" }],
				receivedAt: "2026-04-14T16:01:00Z",
				hasAttachments: false,
				bodyText:
					"Hi Anna — great to hear from you. Chicago oath ceremonies are typically scheduled 2–6 weeks after an approved interview, so you're on the earlier end of that window. The notice will come by mail and also show up in your USCIS online account. As soon as it arrives, please forward it to me and we'll confirm the date and what to bring. No action needed on your end right now.\n\nPriya",
			},
		],
	},
	{
		id: "thr-005",
		conversationId: "conv-aarav-biom-waiver",
		userId: "user-priya",
		subject: "I-751 — do I need biometrics?",
		hasReply: true,
		matterMatch: { matterId: "matter-patel-i751" },
		tags: [
			{ label: "general question", axis: "intent" },
			{ label: "existing client", axis: "who" },
		],
		messages: [
			{
				id: "msg-005-1",
				direction: "inbound",
				from: { name: "Aarav Patel", email: "aarav.patel@example.com" },
				to: [TIM_EMAIL],
				receivedAt: "2026-04-11T19:18:00Z",
				hasAttachments: false,
				bodyText:
					"Priya — quick question. I read online that USCIS is waiving biometrics for some I-751s right now. Is that my case? I don't want to plan time off for an appointment that isn't going to happen.",
			},
			{
				id: "msg-005-2",
				direction: "outbound",
				from: TIM_EMAIL,
				to: [{ name: "Aarav Patel", email: "aarav.patel@example.com" }],
				receivedAt: "2026-04-11T19:42:00Z",
				hasAttachments: false,
				bodyText:
					"Hi Aarav — USCIS is in fact waiving biometrics for many I-751 joint petitions, but it's not guaranteed. Your prior biometrics from the 2023 conditional green card process are typically reused. I'd hold off on planning time off until we see either a notice or the waiver confirmation in your online account. I'll flag the moment either one shows up.\n\nPriya",
			},
		],
	},
	{
		id: "thr-006",
		conversationId: "conv-general-question-h1b",
		userId: "user-priya",
		subject: "H-1B lottery question",
		hasReply: true,
		matterMatch: undefined,
		tags: [
			{ label: "general question", axis: "intent" },
			{ label: "prospective client", axis: "who" },
		],
		fakeDraft: {
			targetMessageId: "msg-006-1",
			model: "claude-sonnet-5",
			promptVersion: "drafter-v0.3 · practice-guide-v0",
			generatedAt: "2026-04-19T19:48:00Z",
			toolCalls: [
				{
					id: "tc-006-1",
					toolName: "lookupMatter",
					title: "Looking up matter",
					input: { senderEmail: "vikram.rao@example.com" },
					output: {
						match: false,
						note: "No matter found. Treat as prospective-client intake.",
					},
				},
				{
					id: "tc-006-2",
					toolName: "searchPracticeGuide",
					title: "Searching practice guide",
					input: { query: "H-1B lottery not selected alternatives" },
					output: {
						source: "§H1B-lottery-not-selected",
						snippet:
							"Canonical alternatives to surface: STEM OPT extension (if degree + E-Verify), O-1, cap-exempt H-1B employers, L-1 (with year abroad), change of status to F-1.",
					},
				},
				{
					id: "tc-006-3",
					toolName: "searchFirmPolicy",
					title: "Searching firm policy",
					input: { query: "new lead cold inquiry response" },
					output: {
						source: "§new-lead-response",
						snippet:
							"For cold inquiries, offer a consultation with fee ($250, credited toward representation). Do not give case-specific legal advice in email.",
					},
				},
			],
			bodyText:
				"Hi Vikram — sorry to hear. A few options to consider quickly:\n\n1. STEM OPT extension if your degree qualifies and your employer is E-Verify enrolled — extends OPT by 24 months.\n2. O-1 extraordinary ability if you meet criteria (less common, high bar).\n3. Consular L-1 if your employer has a qualifying affiliate abroad and you could work there for 1 year.\n4. Enrollment in another degree program to switch back to F-1.\n5. Cap-exempt H-1B employers (some universities, nonprofits) are year-round.\n\nHappy to set up a consultation to walk through specifics. Reply with a few times that work this week.\n\nPriya",
		},
		messages: [
			{
				id: "msg-006-1",
				direction: "inbound",
				from: { name: "Vikram Iyer", email: "vikram.iyer@example.com" },
				to: [TIM_EMAIL],
				receivedAt: "2026-04-12T13:02:00Z",
				hasAttachments: false,
				bodyText:
					"Hello — my employer said I wasn't selected in the H-1B lottery this year. Can I still work? I'm on an F-1 OPT right now but it expires in August. What are my options?\n\nThanks,\nVikram",
			},
			{
				id: "msg-006-2",
				direction: "outbound",
				from: TIM_EMAIL,
				to: [{ name: "Vikram Iyer", email: "vikram.iyer@example.com" }],
				receivedAt: "2026-04-12T14:30:00Z",
				hasAttachments: false,
				bodyText:
					"Hi Vikram — sorry to hear. A few options to consider quickly:\n\n1. STEM OPT extension if your degree qualifies and your employer is E-Verify enrolled — extends OPT by 24 months.\n2. O-1 extraordinary ability if you meet criteria (less common, high bar).\n3. Consular L-1 if your employer has a qualifying affiliate abroad and you could work there for 1 year.\n4. Enrollment in another degree program to switch back to F-1.\n5. Cap-exempt H-1B employers (some universities, nonprofits) are year-round.\n\nHappy to set up a consultation to walk through specifics. Reply with a few times that work this week.\n\nPriya",
			},
		],
	},
	{
		id: "thr-007",
		conversationId: "conv-scheduling-consult",
		userId: "user-priya",
		subject: "Would like to schedule a consult",
		hasReply: true,
		matterMatch: undefined,
		tags: [
			{ label: "scheduling", axis: "intent" },
			{ label: "prospective client", axis: "who" },
		],
		messages: [
			{
				id: "msg-007-1",
				direction: "inbound",
				from: { name: "Fatima Hassan", email: "fatima.h@example.com" },
				to: [TIM_EMAIL],
				receivedAt: "2026-04-13T11:10:00Z",
				hasAttachments: false,
				bodyText:
					"Hello, my brother referred me to your firm. I'd like to schedule a consultation about green card options for my parents. I work full time so evenings or Fridays are best for me.",
			},
			{
				id: "msg-007-2",
				direction: "outbound",
				from: TIM_EMAIL,
				to: [{ name: "Fatima Hassan", email: "fatima.h@example.com" }],
				receivedAt: "2026-04-13T11:55:00Z",
				hasAttachments: false,
				bodyText:
					"Hi Fatima — glad to help. We have Thursday 4/24 at 5:30 PM or Friday 4/25 at 2:00 PM available for an initial 45-minute consultation. Consultation fee is $250 which is credited toward representation if you retain us. Which works?\n\nPriya",
			},
			{
				id: "msg-007-3",
				direction: "inbound",
				from: { name: "Fatima Hassan", email: "fatima.h@example.com" },
				to: [TIM_EMAIL],
				receivedAt: "2026-04-13T20:40:00Z",
				hasAttachments: false,
				bodyText: "Friday 4/25 at 2:00 PM works — please send the invite.",
			},
		],
	},
	{
		id: "thr-008",
		conversationId: "conv-doc-submission-silva",
		userId: "user-priya",
		subject: "Here's my updated address",
		hasReply: true,
		matterMatch: { matterId: "matter-silva-i485" },
		tags: [
			{ label: "document submission", axis: "intent" },
			{ label: "existing client", axis: "who" },
		],
		messages: [
			{
				id: "msg-008-1",
				direction: "inbound",
				from: { name: "Roberto Silva", email: "roberto.silva@example.com" },
				to: [TIM_EMAIL],
				receivedAt: "2026-04-09T09:22:00Z",
				hasAttachments: true,
				bodyText:
					"Priya — as we discussed, here's my new address: 1421 NW 8th Ave, Apt 3, Miami FL 33125. Effective last Monday. I've attached a copy of my lease. Let me know if you need anything else.\n\nRoberto",
			},
			{
				id: "msg-008-2",
				direction: "outbound",
				from: TIM_EMAIL,
				to: [{ name: "Roberto Silva", email: "roberto.silva@example.com" }],
				receivedAt: "2026-04-09T11:05:00Z",
				hasAttachments: false,
				bodyText:
					"Thanks Roberto — received. I'll file an AR-11 for you this week and also update your online account. You should keep a copy of the filed AR-11 confirmation in case a biometrics notice was mis-mailed.\n\nPriya",
			},
		],
	},
	{
		id: "thr-009",
		conversationId: "conv-autoreply",
		userId: "user-priya",
		subject: "Automatic reply: Thanks for reaching out",
		hasReply: false,
		matterMatch: undefined,
		tags: [
			{ label: "auto-reply", axis: "intent" },
			{ label: "noise", axis: "intent" },
		],
		messages: [
			{
				id: "msg-009-1",
				direction: "inbound",
				from: {
					name: "USCIS System",
					email: "uscis-account-noreply@uscis.dhs.gov",
				},
				to: [TIM_EMAIL],
				receivedAt: "2026-04-17T06:00:00Z",
				hasAttachments: false,
				bodyText:
					"This is an automated message from the USCIS online account system. Do not reply to this message. A new case update is available. Log in to view details.",
			},
		],
	},
	{
		id: "thr-010",
		conversationId: "conv-collections",
		userId: "user-priya",
		subject: "Invoice #4482 — past due",
		hasReply: false,
		matterMatch: { matterId: "matter-wilson-k1" },
		tags: [
			{ label: "collections", axis: "intent" },
			{ label: "billing", axis: "intent" },
			{ label: "existing client", axis: "who" },
		],
		messages: [
			{
				id: "msg-010-1",
				direction: "inbound",
				from: { name: "Clio Billing", email: "billing@clio.com" },
				to: [TIM_EMAIL],
				receivedAt: "2026-04-17T08:00:00Z",
				hasAttachments: true,
				bodyText:
					"Invoice #4482 for James Wilson is now 15 days past due ($1,250.00). Automated reminder sent to client on 2026-04-14. Consider a follow-up or payment plan.",
			},
		],
	},
	{
		id: "thr-011",
		conversationId: "conv-wei-lca-question",
		userId: "user-priya",
		subject: "Quick question about LCA posting",
		hasReply: true,
		matterMatch: { matterId: "matter-chen-h1b" },
		tags: [
			{ label: "specific case question", axis: "intent" },
			{ label: "pre-filing", axis: "stage" },
			{ label: "existing client", axis: "who" },
		],
		messages: [
			{
				id: "msg-011-1",
				direction: "inbound",
				from: { name: "Wei Chen", email: "wei.chen@example.com" },
				to: [TIM_EMAIL],
				receivedAt: "2026-04-10T23:17:00Z",
				hasAttachments: false,
				bodyText:
					"Priya — Stripe posted the LCA in the break room at the SF office on Monday. HR took a photo with timestamp. Is that sufficient or do they need to leave it up for a specific number of days?",
			},
			{
				id: "msg-011-2",
				direction: "outbound",
				from: TIM_EMAIL,
				to: [{ name: "Wei Chen", email: "wei.chen@example.com" }],
				receivedAt: "2026-04-11T08:50:00Z",
				hasAttachments: false,
				bodyText:
					"Wei — great. DOL requires posting for 10 consecutive business days at the worksite. The dated photo is exactly what we want for the record. Please have HR keep the posting up until 2026-04-24 and send a second photo of the posting on its last day. We'll need both for the H-1B packet.\n\nPriya",
			},
		],
	},
	{
		id: "thr-012",
		conversationId: "conv-timeline-q-generic",
		userId: "user-priya",
		subject: "How long does a green card take right now?",
		hasReply: true,
		matterMatch: undefined,
		tags: [
			{ label: "general question", axis: "intent" },
			{ label: "timeline question", axis: "intent" },
			{ label: "prospective client", axis: "who" },
		],
		messages: [
			{
				id: "msg-012-1",
				direction: "inbound",
				from: { name: "Nadia Ahmadi", email: "nadia.a@example.com" },
				to: [TIM_EMAIL],
				receivedAt: "2026-04-08T18:05:00Z",
				hasAttachments: false,
				bodyText:
					"Hi, we haven't worked together yet. My husband and I are both US citizens and we're thinking of sponsoring my mom. How long would a green card take for a parent of a US citizen?",
			},
			{
				id: "msg-012-2",
				direction: "outbound",
				from: TIM_EMAIL,
				to: [{ name: "Nadia Ahmadi", email: "nadia.a@example.com" }],
				receivedAt: "2026-04-09T09:14:00Z",
				hasAttachments: false,
				bodyText:
					'Hi Nadia — parents of US citizens are "immediate relatives" and are not subject to annual visa caps. Current typical timelines, depending on whether your mom is inside the US (adjustment of status) or abroad (consular processing), are roughly 12–18 months from filing to green card, though service centers vary. Happy to set up a consult to walk through the specifics of your mother\'s situation.\n\nPriya',
			},
		],
	},
	{
		id: "thr-013",
		conversationId: "conv-no-reply-forwarded",
		userId: "user-priya",
		subject: "FW: Case inquiry — my cousin",
		hasReply: false,
		matterMatch: undefined,
		tags: [
			{ label: "forwarded internally", axis: "intent" },
			{ label: "new lead", axis: "intent" },
		],
		messages: [
			{
				id: "msg-013-1",
				direction: "inbound",
				from: { name: "Diego Morales", email: "diego@martinezlaw.com" },
				to: [TIM_EMAIL],
				receivedAt: "2026-04-15T10:20:00Z",
				hasAttachments: false,
				bodyText:
					"Forwarding for triage — caller's cousin has an asylum matter. Not our usual scope but I said we'd review. Let me know.\n\nDiego",
			},
		],
	},
	{
		id: "thr-014",
		conversationId: "conv-anna-thank-you",
		userId: "user-priya",
		subject: "Thank you",
		hasReply: false,
		matterMatch: { matterId: "matter-kowalski-n400" },
		tags: [
			{ label: "positive feedback", axis: "intent" },
			{ label: "existing client", axis: "who" },
		],
		messages: [
			{
				id: "msg-014-1",
				direction: "inbound",
				from: { name: "Anna Kowalski", email: "anna.k.chicago@example.com" },
				to: [TIM_EMAIL],
				cc: [MARCUS_EMAIL],
				receivedAt: "2026-03-29T19:11:00Z",
				hasAttachments: false,
				bodyText:
					"Priya and Marcus — I just wanted to say thank you. Eight years is a long time, and yesterday felt surreal. I couldn't have done this without both of you. I'll send cookies as soon as I stop crying.\n\nAnna",
			},
		],
	},

	// Jennifer's inbox
	{
		id: "thr-020",
		conversationId: "conv-james-evidence-q",
		userId: "user-jennifer",
		subject: "What counts as relationship evidence?",
		hasReply: true,
		matterMatch: { matterId: "matter-wilson-k1" },
		tags: [
			{ label: "specific case question", axis: "intent" },
			{ label: "post-refusal", axis: "stage" },
			{ label: "existing client", axis: "who" },
		],
		messages: [
			{
				id: "msg-020-1",
				direction: "inbound",
				from: { name: "James Wilson", email: "james.wilson@example.com" },
				to: [JENNIFER_EMAIL],
				receivedAt: "2026-04-17T02:10:00Z",
				hasAttachments: false,
				bodyText:
					"Jennifer — following up on the 221(g). What specifically counts as good relationship evidence at the post-refusal stage? I have everything we already sent, plus more chat logs. But I'm worried about being repetitive. Do I need notarized statements? Statements from my parents?",
			},
			{
				id: "msg-020-2",
				direction: "outbound",
				from: JENNIFER_EMAIL,
				to: [{ name: "James Wilson", email: "james.wilson@example.com" }],
				receivedAt: "2026-04-17T10:05:00Z",
				hasAttachments: false,
				bodyText:
					"James — we want to emphasize *in-person* proof this round, since the officer flagged the relationship as primarily digital:\n\n• Flight itineraries / boarding passes for all three of your visits\n• Hotel booking receipts with both names where possible\n• Dated photos with location metadata (our of restaurants, family dinners, tourist spots)\n• Any joint receipts (meals, activities, gifts)\n• Short (1 page) statements from 2–3 family/close friends who have met Thanh or have heard of/spoken with her on video calls — notarized is ideal but not required\n\nDon't re-submit everything we already sent. We'll reference that original packet and focus the new submission on filling the specific gap.\n\nI'll send a structured list by end of day tomorrow.\n\nJennifer",
			},
		],
	},
	{
		id: "thr-021",
		conversationId: "conv-sofia-vawa-j-thread",
		userId: "user-jennifer",
		subject: "Re: asking for a friend - immigration question",
		hasReply: false,
		matterMatch: { matterId: "matter-reyes-vawa" },
		tags: [
			{ label: "forwarded internally", axis: "intent" },
			{ label: "sensitive", axis: "intent" },
		],
		messages: [
			{
				id: "msg-021-1",
				direction: "inbound",
				from: TIM_EMAIL,
				to: [JENNIFER_EMAIL],
				receivedAt: "2026-04-17T08:30:00Z",
				hasAttachments: false,
				bodyText:
					"Jennifer — forwarding. This reads VAWA to me. Inquirer is a third party (sister). Possible DV situation. Please handle intake with extra care — don't leave VM, use sister's contact, coordinate with Marcus before any written response goes out. Thanks. — Priya",
			},
		],
	},
	{
		id: "thr-022",
		conversationId: "conv-rfe-notice-forward",
		userId: "user-jennifer",
		subject: "Received RFE for Patel I-130",
		hasReply: true,
		matterMatch: undefined,
		tags: [
			{ label: "RFE", axis: "intent" },
			{ label: "existing client", axis: "who" },
		],
		messages: [
			{
				id: "msg-022-1",
				direction: "inbound",
				from: { name: "Raj Patel", email: "raj.patel@example.com" },
				to: [JENNIFER_EMAIL],
				receivedAt: "2026-04-16T14:22:00Z",
				hasAttachments: true,
				bodyText:
					'Jennifer — we got an RFE in the mail today. Two pages, asking for more evidence on the "bona fide marriage." Scanned and attached. Please let us know next steps.\n\nRaj & Priya Patel',
			},
			{
				id: "msg-022-2",
				direction: "outbound",
				from: JENNIFER_EMAIL,
				to: [{ name: "Raj Patel", email: "raj.patel@example.com" }],
				cc: [MARCUS_EMAIL],
				receivedAt: "2026-04-16T16:40:00Z",
				hasAttachments: false,
				bodyText:
					"Raj — received, thanks for the quick scan. I'll review tonight and we'll have a response plan to you within 48 hours. The deadline on the RFE should be at least 84 days — still plenty of time, no panic. — Jennifer",
			},
		],
	},
	{
		id: "thr-023",
		conversationId: "conv-autoforward-out-of-office",
		userId: "user-jennifer",
		subject: "Out of office: Henrik Dahl",
		hasReply: false,
		matterMatch: undefined,
		tags: [
			{ label: "auto-reply", axis: "intent" },
			{ label: "noise", axis: "intent" },
		],
		messages: [
			{
				id: "msg-023-1",
				direction: "inbound",
				from: { name: "Henrik Dahl", email: "henrik.dahl@example.com" },
				to: [JENNIFER_EMAIL],
				receivedAt: "2026-04-11T04:00:00Z",
				hasAttachments: false,
				bodyText:
					"I'm traveling through 4/22 with limited email access. For urgent matters please contact my assistant Cara at cara.lund@example.com.",
			},
		],
	},
	{
		id: "thr-024",
		conversationId: "conv-intake-survey",
		userId: "user-jennifer",
		subject: "Re: Intake form — Diaz",
		hasReply: true,
		matterMatch: undefined,
		tags: [
			{ label: "intake", axis: "intent" },
			{ label: "prospective client", axis: "who" },
		],
		messages: [
			{
				id: "msg-024-1",
				direction: "inbound",
				from: { name: "Luis Diaz", email: "luis.diaz@example.com" },
				to: [JENNIFER_EMAIL],
				receivedAt: "2026-04-08T12:03:00Z",
				hasAttachments: true,
				bodyText:
					"Jennifer — attached is the completed intake form and a copy of my prior I-94. Let me know what else you need for the consult on Friday.",
			},
			{
				id: "msg-024-2",
				direction: "outbound",
				from: JENNIFER_EMAIL,
				to: [{ name: "Luis Diaz", email: "luis.diaz@example.com" }],
				receivedAt: "2026-04-08T13:20:00Z",
				hasAttachments: false,
				bodyText:
					"Luis — received. For Friday please also bring: your current passport, any prior USCIS notices, and the I-20s from your time on F-1. See you at 2 PM.\n\nJennifer",
			},
		],
	},
	{
		id: "thr-025",
		conversationId: "conv-paralegal-qna",
		userId: "user-jennifer",
		subject: "Question on affidavit of support",
		hasReply: false,
		matterMatch: undefined,
		tags: [{ label: "internal", axis: "intent" }],
		messages: [
			{
				id: "msg-025-1",
				direction: "inbound",
				from: { name: "Priya Patel", email: "priya@martinezlaw.com" },
				to: [JENNIFER_EMAIL],
				receivedAt: "2026-04-17T16:44:00Z",
				hasAttachments: false,
				bodyText:
					"Jen — quick q. On the Silva I-864, do we include Jessica's 2023 tax return or just the most recent? I've always done 3 years but MC said last month he wants just 1 unless recent income is an outlier. Want to be consistent.\n\nP",
			},
		],
	},

	// Marcus's inbox
	{
		id: "thr-030",
		conversationId: "conv-attorney-escalation",
		userId: "user-marcus",
		subject: "Urgent — possible deportation question",
		hasReply: true,
		matterMatch: undefined,
		tags: [
			{ label: "urgent", axis: "intent" },
			{ label: "escalation", axis: "intent" },
			{ label: "prospective client", axis: "who" },
		],
		messages: [
			{
				id: "msg-030-1",
				direction: "inbound",
				from: { name: "Ahmed Farooq", email: "ahmed.f@example.com" },
				to: [MARCUS_EMAIL],
				receivedAt: "2026-04-18T08:05:00Z",
				hasAttachments: false,
				bodyText:
					"Mr. Chen — I was given your name by another attorney. My brother was detained by ICE yesterday at his workplace. He has a green card from 2018 but was convicted of a DUI in 2022. What are our options? He has two young children who are US citizens.",
			},
			{
				id: "msg-030-2",
				direction: "outbound",
				from: MARCUS_EMAIL,
				to: [{ name: "Ahmed Farooq", email: "ahmed.f@example.com" }],
				receivedAt: "2026-04-18T08:28:00Z",
				hasAttachments: false,
				bodyText:
					"Ahmed — I want to talk to you today. I'm calling you in 10 minutes at the number on your signature. While I do — please gather: the exact charge and disposition of the DUI (plea, sentence), his A-number, the location where he is being held if you know it, and the names of the children.\n\nMarcus",
			},
		],
	},
	{
		id: "thr-031",
		conversationId: "conv-from-partner-firm",
		userId: "user-marcus",
		subject: "Co-counsel question on PERM denial",
		hasReply: true,
		matterMatch: undefined,
		tags: [
			{ label: "professional inquiry", axis: "intent" },
			{ label: "referral", axis: "who" },
		],
		messages: [
			{
				id: "msg-031-1",
				direction: "inbound",
				from: { name: "Elena Rivers", email: "elena@riverslaw.com" },
				to: [MARCUS_EMAIL],
				receivedAt: "2026-04-15T16:30:00Z",
				hasAttachments: true,
				bodyText:
					"Marcus — attaching a PERM denial from DOL BALCA we'd like a second read on before we appeal. Would appreciate your thoughts on whether the prevailing wage issue is worth fighting vs. refiling. Happy to pay hourly.\n\nElena",
			},
			{
				id: "msg-031-2",
				direction: "outbound",
				from: MARCUS_EMAIL,
				to: [{ name: "Elena Rivers", email: "elena@riverslaw.com" }],
				receivedAt: "2026-04-15T19:45:00Z",
				hasAttachments: false,
				bodyText:
					"Elena — looking now. Will circle back tomorrow morning with thoughts. Rate is $450/hr for second reads; let me know if that works before I dive in further.\n\nMarcus",
			},
		],
	},
	{
		id: "thr-032",
		conversationId: "conv-marcus-wilson-cc",
		userId: "user-marcus",
		subject: "Re: Fiancee visa interview didn't go well",
		hasReply: false,
		matterMatch: { matterId: "matter-wilson-k1" },
		tags: [
			{ label: "cc", axis: "intent" },
			{ label: "existing client", axis: "who" },
		],
		messages: [
			{
				id: "msg-032-1",
				direction: "inbound",
				from: { name: "James Wilson", email: "james.wilson@example.com" },
				to: [TIM_EMAIL],
				cc: [MARCUS_EMAIL],
				receivedAt: "2026-04-15T21:50:36Z",
				hasAttachments: false,
				bodyText:
					"(This thread was cc'd to Marcus — full thread is on Priya's side.)",
			},
		],
	},
	{
		id: "thr-033",
		conversationId: "conv-bar-association",
		userId: "user-marcus",
		subject: "AILA annual conference — registration reminder",
		hasReply: false,
		matterMatch: undefined,
		tags: [
			{ label: "professional / marketing", axis: "intent" },
			{ label: "noise", axis: "intent" },
		],
		messages: [
			{
				id: "msg-033-1",
				direction: "inbound",
				from: { name: "AILA Events", email: "events@aila.org" },
				to: [MARCUS_EMAIL],
				receivedAt: "2026-04-12T00:15:00Z",
				hasAttachments: false,
				bodyText:
					"Early-bird registration for AILA Annual closes this Friday. Your member rate is $795.",
			},
		],
	},
	{
		id: "thr-034",
		conversationId: "conv-marcus-internal-review",
		userId: "user-marcus",
		subject: "Weekly case review — this Friday",
		hasReply: true,
		matterMatch: undefined,
		tags: [{ label: "internal", axis: "intent" }],
		messages: [
			{
				id: "msg-034-1",
				direction: "inbound",
				from: { name: "Priya Patel", email: "priya@martinezlaw.com" },
				to: [MARCUS_EMAIL],
				cc: [JENNIFER_EMAIL],
				receivedAt: "2026-04-15T14:20:00Z",
				hasAttachments: false,
				bodyText:
					"Marcus — pulled the list for Friday's review: Silva (biometrics delay), Wilson (221g active), Patel I-751 (monitoring), Chen H-1B (filing 4/26). Kowalski moves to closed once oath lands. Anything to add?\n\nPriya",
			},
			{
				id: "msg-034-2",
				direction: "outbound",
				from: MARCUS_EMAIL,
				to: [{ name: "Priya Patel", email: "priya@martinezlaw.com" }],
				cc: [JENNIFER_EMAIL],
				receivedAt: "2026-04-15T14:45:00Z",
				hasAttachments: false,
				bodyText:
					"Add VAWA inquiry from Sofia Reyes. We should decide by Friday whether we're taking on.\n\nMC",
			},
		],
	},
];
